import {
  ref,
  get,
  set,
  update,
  remove,
  push,
  onValue,
  query,
  orderByChild,
  equalTo,
  off,
} from 'firebase/database';
import { rtdb } from '../../config/firebase';
import type { BragPost, BragComment } from '../../types';
import { dateToMs, msToDate, snapshotToArray, stripUndefined } from './helpers';

const PATH = 'bragPosts';

type Unsubscribe = () => void;

const commentsFromRtdb = (raw: any): BragPost['comments'] => {
  if (!raw || typeof raw !== 'object') return undefined;
  const result: { [id: string]: BragComment } = {};
  for (const [cid, cdata] of Object.entries<any>(raw)) {
    result[cid] = { id: cid, userId: cdata.userId, authorName: cdata.authorName, text: cdata.text, timestamp: msToDate(cdata.timestamp) as Date };
  }
  return result;
};

const fromRtdb = (id: string, data: any): BragPost => ({
  ...data,
  id,
  timestamp: msToDate(data.timestamp) as Date,
  reactions: data.reactions || {},
  comments: commentsFromRtdb(data.comments),
});

const toRtdb = (brag: BragPost): any => {
  const { id: _id, ...rest } = brag;
  return {
    ...rest,
    timestamp: dateToMs(brag.timestamp),
    reactions: brag.reactions || {},
  };
};

const sortByTimestampDesc = (brags: BragPost[]): BragPost[] =>
  [...brags].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

export const getAllBrags = async (): Promise<BragPost[]> => {
  const snapshot = await get(ref(rtdb, PATH));
  return sortByTimestampDesc(
    snapshotToArray<BragPost>(snapshot.val(), fromRtdb)
  );
};

export const getBragById = async (bragId: string): Promise<BragPost | null> => {
  const snapshot = await get(ref(rtdb, `${PATH}/${bragId}`));
  return snapshot.exists() ? fromRtdb(bragId, snapshot.val()) : null;
};

export const saveBrag = async (brag: BragPost): Promise<void> => {
  await set(ref(rtdb, `${PATH}/${brag.id}`), stripUndefined(toRtdb(brag)));
};

export const updateBrag = async (
  bragId: string,
  updates: Partial<BragPost>
): Promise<void> => {
  const data: any = { ...updates };
  if (updates.timestamp !== undefined) {
    data.timestamp = dateToMs(updates.timestamp);
  }
  await update(ref(rtdb, `${PATH}/${bragId}`), stripUndefined(data));
};

export const deleteBrag = async (bragId: string): Promise<void> => {
  await remove(ref(rtdb, `${PATH}/${bragId}`));
};

export const getBragsByHomeId = async (homeId: string): Promise<BragPost[]> => {
  const q = query(ref(rtdb, PATH), orderByChild('homeId'), equalTo(homeId));
  const snapshot = await get(q);
  return sortByTimestampDesc(
    snapshotToArray<BragPost>(snapshot.val(), fromRtdb)
  );
};

export const getBragsByUserId = async (userId: string): Promise<BragPost[]> => {
  const q = query(ref(rtdb, PATH), orderByChild('userId'), equalTo(userId));
  const snapshot = await get(q);
  return sortByTimestampDesc(
    snapshotToArray<BragPost>(snapshot.val(), fromRtdb)
  );
};

export const addReaction = async (
  bragId: string,
  userId: string,
  reaction: 'like' | 'dislike'
): Promise<void> => {
  await update(ref(rtdb, `${PATH}/${bragId}/reactions`), { [userId]: reaction });
};

export const removeReaction = async (
  bragId: string,
  userId: string
): Promise<void> => {
  await remove(ref(rtdb, `${PATH}/${bragId}/reactions/${userId}`));
};

/**
 * Toggle reaction (add if doesn't exist, remove if same, change if different)
 */
export const toggleReaction = async (
  bragId: string,
  userId: string,
  reaction: 'like' | 'dislike'
): Promise<void> => {
  const snapshot = await get(ref(rtdb, `${PATH}/${bragId}/reactions/${userId}`));
  const current = snapshot.val();
  if (current === reaction) {
    await remove(ref(rtdb, `${PATH}/${bragId}/reactions/${userId}`));
  } else {
    await update(ref(rtdb, `${PATH}/${bragId}/reactions`), {
      [userId]: reaction,
    });
  }
};

// ── Comments ─────────────────────────────────────────────────────────

export const addComment = async (
  bragId: string,
  userId: string,
  text: string
): Promise<void> => {
  const commentRef = push(ref(rtdb, `${PATH}/${bragId}/comments`));
  await set(commentRef, stripUndefined({ userId, text, timestamp: Date.now() }));
};

export const deleteComment = async (
  bragId: string,
  commentId: string
): Promise<void> => {
  await remove(ref(rtdb, `${PATH}/${bragId}/comments/${commentId}`));
};

export const subscribeToBrags = (
  callback: (brags: BragPost[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const r = ref(rtdb, PATH);
  const handler = onValue(
    r,
    (snapshot) => {
      callback(
        sortByTimestampDesc(snapshotToArray<BragPost>(snapshot.val(), fromRtdb))
      );
    },
    onError
  );
  return () => off(r, 'value', handler);
};

export const subscribeToHomeBrags = (
  homeId: string,
  callback: (brags: BragPost[]) => void
): Unsubscribe => {
  const q = query(ref(rtdb, PATH), orderByChild('homeId'), equalTo(homeId));
  const handler = onValue(q, (snapshot) => {
    callback(
      sortByTimestampDesc(snapshotToArray<BragPost>(snapshot.val(), fromRtdb))
    );
  });
  return () => off(q, 'value', handler);
};

export const subscribeToUserBrags = (
  userId: string,
  callback: (brags: BragPost[]) => void
): Unsubscribe => {
  const q = query(ref(rtdb, PATH), orderByChild('userId'), equalTo(userId));
  const handler = onValue(q, (snapshot) => {
    callback(
      sortByTimestampDesc(snapshotToArray<BragPost>(snapshot.val(), fromRtdb))
    );
  });
  return () => off(q, 'value', handler);
};
